
import { useState, useEffect } from "react";
import { BorrowerCard } from "./BorrowerCard";
import { LoanFilters } from "./filters/LoanFilters";
import { CollateralCalculator } from "./calculator/CollateralCalculator";
import { Loan } from "@/types/borrower";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSessionContext } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";

interface BorrowersListProps {
  loans: Loan[];
}

export const BorrowersList = ({ loans }: BorrowersListProps) => {
  const [loanType, setLoanType] = useState<string>("all");
  const [hasCollateral, setHasCollateral] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [minAmount, setMinAmount] = useState<number>(0);
  const [maxAmount, setMaxAmount] = useState<number>(50000);
  const [sortBy, setSortBy] = useState<string>("newest");
  
  const isMobile = useIsMobile();
  const { session } = useSessionContext();
  const [isBorrower, setIsBorrower] = useState(false);

  useEffect(() => {
    const checkUserRole = async () => {
      if (session?.user?.id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', session.user.id)
          .single();
        
        setIsBorrower(profile?.role === 'borrower');
      }
    };

    checkUserRole();
  }, [session]);

  const filteredLoans = loans?.filter((loan) => {
    // Filter by loan type
    const matchesType = loanType === "all" || 
      (loanType === "business" && loan.category === "business") ||
      (loanType === "personal" && loan.category !== "business");
    
    // Filter by collateral
    const matchesCollateral = hasCollateral === "all" ||
      (hasCollateral === "true" && loan.collateral) ||
      (hasCollateral === "false" && !loan.collateral);
    
    // Filter by search term
    const matchesSearch = searchTerm === "" ||
      loan.purpose?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.story?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.borrower?.profile?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.borrower?.profile?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.borrower?.profile?.business_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by amount
    const matchesAmount = loan.amount >= minAmount && loan.amount <= maxAmount;
    
    return matchesType && matchesCollateral && matchesSearch && matchesAmount;
  }).sort((a, b) => {
    // Sort functionality
    switch (sortBy) {
      case "newest":
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case "oldest":
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      case "amount_high":
        return b.amount - a.amount;
      case "amount_low":
        return a.amount - b.amount;
      case "funding":
        return (b.funding_progress / b.amount) - (a.funding_progress / a.amount);
      default:
        return 0;
    }
  });

  // Duplicate loans to create a larger grid when there are few loans
  const loansToDisplay = filteredLoans?.length < 6 && loans?.length > 0
    ? [...filteredLoans, ...loans.slice(0, Math.min(9 - filteredLoans.length, loans.length))]
    : filteredLoans;

  return (
    <div className="space-y-4 md:space-y-6 lg:space-y-8">
      <div className={`grid gap-4 md:gap-6 lg:gap-8 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 xl:grid-cols-4'}`}>
        <div className={`${isMobile ? 'order-2' : 'xl:col-span-3'}`}>
          <LoanFilters 
            onFilterChange={setLoanType}
            onCollateralFilterChange={(value) => setHasCollateral(value ? "true" : "false")}
            onSearchChange={setSearchTerm}
            onAmountFilterChange={(min, max) => {
              setMinAmount(min);
              setMaxAmount(max);
            }}
            onSortChange={setSortBy}
          />
          
          {loansToDisplay?.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No matching loans found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:gap-6 lg:gap-8 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {loansToDisplay?.map((loan, index) => (
                <BorrowerCard key={`${loan.id}-${index}`} loan={loan} />
              ))}
            </div>
          )}
        </div>
        <div className={`${isMobile ? 'order-1' : 'xl:col-span-1'}`}>
          <CollateralCalculator />
        </div>
      </div>
    </div>
  );
};
